import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { windowLabel } from "@/lib/api";
import { CheckCircle2, Clock, XCircle, Footprints } from "lucide-react";

export default function Confirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed
  const [order, setOrder] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (attemptsRef.current >= 8) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (orderId) {
            const o = await api.get(`/orders/${orderId}`);
            setOrder(o.data);
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId, orderId]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="card-clay p-8 text-center">
        {status === "checking" && (
          <>
            <Clock className="mx-auto text-slate-500" size={48} />
            <h1 className="font-fredoka text-3xl font-bold mt-3" data-testid="confirm-status-checking">Confirming your payment…</h1>
            <p className="text-slate-600 mt-2">Hang tight — this only takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto w-20 h-20 rounded-3xl bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="text-green-600" size={48} strokeWidth={2.5} />
            </div>
            <h1 className="font-fredoka text-3xl font-bold mt-4 text-slate-900" data-testid="confirm-status-paid">You're all set! 🎉</h1>
            <p className="text-slate-600 mt-2">
              Thanks for booking with Bag Dash Kids. We'll send a confirmation by email or text shortly.
            </p>
            {order && (
              <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-left">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Pickup details</div>
                <div className="mt-2 text-sm text-slate-700 space-y-1">
                  <div><b>When:</b> {windowLabel(order.window)}</div>
                  <div><b>Where:</b> {order.community} · Bldg {order.building} · Unit {order.unit}</div>
                  <div><b>Bags:</b> {order.bags}{order.monthly_cleaning ? " + monthly bin cleaning" : ""}</div>
                  <div><b>Total paid:</b> ${order.total_amount.toFixed(2)}</div>
                </div>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard" className="btn-push-green px-5 py-2.5" data-testid="confirm-go-dashboard">View My Pickups</Link>
              <Link to="/" className="btn-push-outline px-5 py-2.5" data-testid="confirm-go-home">Back to home</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "failed" || status === "timeout") && (
          <>
            <XCircle className="mx-auto text-red-500" size={48} strokeWidth={2.5}/>
            <h1 className="font-fredoka text-3xl font-bold mt-3" data-testid="confirm-status-failed">Payment not completed</h1>
            <p className="text-slate-600 mt-2">
              {status === "expired" ? "The checkout session expired." : "We couldn't confirm your payment."} You can try again from your order page.
            </p>
            {orderId && (
              <Link to={`/checkout/${orderId}`} className="btn-push-green inline-block mt-5 px-5 py-2.5">Try again</Link>
            )}
          </>
        )}
        <div className="mt-8 inline-flex items-center gap-2 text-xs text-slate-400">
          <Footprints size={14} strokeWidth={2.5}/> Bag Dash Kids
        </div>
      </div>
    </div>
  );
}
